import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router'
import { lazy, Suspense } from 'react'
import { Loader } from '@grofers/dock-design-kit'
import { APP_ROUTES, LEGACY_META_BRAND_REDIRECTS, LEGACY_ROUTE_REDIRECTS } from './routes.constants'
import ProtectedRoute from './ProtectedRoute'
import { LegacyMetaBrandRedirect, MetaBrandRoute, ShoppableBrandRoute } from './BrandRoutes'

const Home = lazy(() => import('../pages/home'))
const SelectPlatform = lazy(() => import('../pages/select-platform'))
const ShoppableLinks = lazy(() => import('../pages/shoppable-links'))
const ShoppableLinksBrand = lazy(() => import('../pages/shoppable-links-brand'))
const NewCampaign = lazy(() => import('../pages/new-campaign'))
const MetaCollab = lazy(() => import('../pages/meta-collab'))
const MetaLink = lazy(() => import('../pages/meta-link'))
const BrandPage = lazy(() => import('../pages/brand-page'))
const LandingPageNew = lazy(() => import('../pages/landing-page'))
const LinkAudiences = lazy(() => import('../pages/link-audiences'))
const BestPracticesPage = lazy(() => import('../pages/best-practices'))
const FaqsPage = lazy(() => import('../pages/faqs'))
const UserManagement = lazy(() => import('../pages/user-requests'))
const CampaignManagement = lazy(() => import('../pages/campaign-management'))

const RedirectWithSearch = ({ to }: { to: string }) => {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

const LoadingFallback = () => (
  <div className="flex h-screen w-full items-center justify-center bg-white">
    <Loader showLoader size="lg" />
  </div>
)

// Simple health check component
const HealthCheck = () => <>OK</>

const routes = createBrowserRouter([
  {
    path: APP_ROUTES.HOME,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Home />
      </Suspense>
    ),
  },
  {
    path: APP_ROUTES.HEALTH,
    element: <HealthCheck />,
  },
  ...Object.entries(LEGACY_ROUTE_REDIRECTS).map(([path, to]) => ({
    path,
    element: <RedirectWithSearch to={to} />,
  })),
  ...Object.entries(LEGACY_META_BRAND_REDIRECTS).map(([path, to]) => ({
    path,
    element: <LegacyMetaBrandRedirect to={to} />,
  })),
  {
    path: APP_ROUTES.SELECT_PLATFORM,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <SelectPlatform />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/shoppable-links',
    element: <RedirectWithSearch to={APP_ROUTES.SHOPPABLE_LINKS} />,
  },
  {
    path: APP_ROUTES.SHOPPABLE_LINKS,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <ShoppableLinks />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: APP_ROUTES.SHOPPABLE_CAMPAIGN_MANAGEMENT,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <CampaignManagement />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: APP_ROUTES.SHOPPABLE_LINKS_BRAND,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <ShoppableBrandRoute>
            <ShoppableLinksBrand />
          </ShoppableBrandRoute>
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: APP_ROUTES.NEW_CAMPAIGN,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <ShoppableBrandRoute>
            <NewCampaign />
          </ShoppableBrandRoute>
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: APP_ROUTES.META_COLLAB,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <MetaCollab />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: APP_ROUTES.META_LINK,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <MetaBrandRoute>
            <MetaLink />
          </MetaBrandRoute>
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: APP_ROUTES.BRAND_PAGE,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <MetaBrandRoute>
            <BrandPage />
          </MetaBrandRoute>
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: APP_ROUTES.BRAND_PAGE_LANDING_PAGE_NEW,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <MetaBrandRoute>
            <LandingPageNew />
          </MetaBrandRoute>
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: APP_ROUTES.NEW_AUDIENCE,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <MetaBrandRoute>
            <LinkAudiences />
          </MetaBrandRoute>
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: APP_ROUTES.VIEW_AUDIENCE,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <MetaBrandRoute>
            <LinkAudiences />
          </MetaBrandRoute>
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: APP_ROUTES.CLONE_AUDIENCE,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <MetaBrandRoute>
            <LinkAudiences />
          </MetaBrandRoute>
        </Suspense>
      </ProtectedRoute>
    ),
  },
  ...[APP_ROUTES.META_BEST_PRACTICES, APP_ROUTES.SHOPPABLE_BEST_PRACTICES].map((path) => ({
    path,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <BestPracticesPage />
        </Suspense>
      </ProtectedRoute>
    ),
  })),
  ...[APP_ROUTES.META_FAQS, APP_ROUTES.SHOPPABLE_FAQS].map((path) => ({
    path,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <FaqsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  })),
  {
    path: APP_ROUTES.FAQS,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <FaqsPage />
      </Suspense>
    ),
  },
  // key by path so switching between these routes remounts the page (fresh initial tab)
  ...[APP_ROUTES.META_BRAND_MANAGEMENT, APP_ROUTES.USER_MANAGEMENT].map((path) => ({
    path,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <UserManagement key={path} />
        </Suspense>
      </ProtectedRoute>
    ),
  })),
  {
    path: '*',
    element: <>404 Not Found</>,
  },
])

const AppRouter = () => {
  return <RouterProvider router={routes} />
}

export default AppRouter
